'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trackConversion } from '@/components/google-ads-tracking';
import { telemetry } from '@/lib/telemetry';

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
}

type AuthView = 'signup' | 'login';

function authUiErrorMeta(error: unknown) {
  const err = error as { code?: string; message?: string };
  return {
    error_code: err?.code || undefined,
    error_message: err?.message || String(error).slice(0, 300),
  };
}

export function AuthModal({ open, onClose }: AuthModalProps) {
  const { signInWithGoogle, signUpWithEmail, signInWithEmail } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Best-effort returnUrl that mirrors the current page so the user lands
  // back where they were after they finish verifying their email.
  const buildReturnUrl = useCallback(() => {
    const path = pathname || '/';
    const qs = searchParams?.toString() ?? '';
    return qs ? `${path}?${qs}` : path;
  }, [pathname, searchParams]);

  const goAfterAuth = useCallback(
    (verified: boolean) => {
      if (!verified) {
        const returnUrl = buildReturnUrl();
        router.push(`/verify-email?returnUrl=${encodeURIComponent(returnUrl)}`);
      }
      onClose();
    },
    [buildReturnUrl, onClose, router],
  );

  const [view, setView] = useState<AuthView>('signup');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      telemetry.emit('auth.modal_open', { source: 'auth_modal', default_view: 'signup' });
      setView('signup');
      setError('');
      setLoading(false);
      setFormData({ name: '', email: '', password: '' });
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleGoogleAuth = useCallback(async () => {
    telemetry.emit('auth.google_click', { source: 'auth_modal', view });
    try {
      setLoading(true);
      setError('');
      const { verified } = await signInWithGoogle();
      if (process.env.NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_LABEL) {
        trackConversion(process.env.NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_LABEL, 1.0);
      }
      goAfterAuth(verified);
    } catch (error: any) {
      if (error?.code !== 'auth/popup-closed-by-user') {
        setError(error?.message || 'Authentication failed. Please try again.');
      }
      telemetry.emit('auth.error', {
        source: 'auth_modal',
        method: 'google',
        view,
        user_cancelled: error?.code === 'auth/popup-closed-by-user',
        ...authUiErrorMeta(error),
      });
    } finally {
      setLoading(false);
    }
  }, [signInWithGoogle, goAfterAuth, view]);

  const handleEmailSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    telemetry.emit('auth.email_submit', {
      source: 'auth_modal',
      view,
      has_name: Boolean(formData.name.trim()),
      email_domain: formData.email.includes('@') ? formData.email.split('@').pop() : undefined,
      password_length_bucket:
        formData.password.length < 6 ? '<6' : formData.password.length < 12 ? '6-11' : '12+',
    });
    try {
      setLoading(true);
      setError('');
      let verified = false;
      if (view === 'signup') {
        const result = await signUpWithEmail(formData.email, formData.password, formData.name);
        verified = result.verified;
        if (process.env.NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_LABEL) {
          trackConversion(process.env.NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_LABEL, 1.0);
        }
      } else {
        const result = await signInWithEmail(formData.email, formData.password);
        verified = result.verified;
      }
      goAfterAuth(verified);
    } catch (error: any) {
      setError(error?.message || 'Authentication failed. Please try again.');
      telemetry.emit('auth.error', {
        source: 'auth_modal',
        method: view === 'signup' ? 'email_signup' : 'email_login',
        view,
        ...authUiErrorMeta(error),
      });
    } finally {
      setLoading(false);
    }
  }, [view, formData, signUpWithEmail, signInWithEmail, goAfterAuth]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => {
            telemetry.emit('auth.modal_close', { source: 'auth_modal', reason: 'backdrop', view });
            onClose();
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 12 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="relative w-full max-w-[400px] mx-4 rounded-xl border border-zinc-800 bg-zinc-950 px-5 pt-3 pb-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => {
                telemetry.emit('auth.modal_close', { source: 'auth_modal', reason: 'x_button', view });
                onClose();
              }}
              className="absolute top-4 right-4 p-1 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50 transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            {/* Logo */}
            <div className="flex justify-center mb-0">
              <Image
                src="/logo.png"
                alt="Xynth"
                width={120}
                height={120}
                className="object-contain"
              />
            </div>

            {/* Headline */}
            <div className="text-center mb-3">
              <h2 className="text-2xl font-bold text-white tracking-tight">
                {view === 'signup' ? 'Find your edge.' : 'Welcome back.'}
              </h2>
              <p className="text-sm text-zinc-500 mt-1">
                {view === 'signup'
                  ? 'Sign up to get started.'
                  : 'Sign in to continue'}
              </p>
            </div>

            {/* Google button */}
            <Button
              type="button"
              onClick={handleGoogleAuth}
              disabled={loading}
              className="w-full h-11 bg-white hover:bg-zinc-200 text-black font-semibold rounded-lg mb-1 transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5 mr-2.5 shrink-0">
                <path
                  d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                  fill="currentColor"
                />
              </svg>
              {loading ? 'Please wait...' : 'Continue with Google'}
            </Button>

            {/* Divider */}
            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-zinc-800" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-zinc-950 px-3 text-zinc-500">or</span>
              </div>
            </div>

            {/* Email form */}
            <form onSubmit={handleEmailSubmit} className="space-y-2">
              {view === 'signup' && (
                <div>
                  <Label htmlFor="auth-name" className="text-xs text-zinc-400 mb-1 block">
                    Full Name
                  </Label>
                  <Input
                    id="auth-name"
                    name="name"
                    type="text"
                    placeholder="Jane Doe"
                    value={formData.name}
                    onChange={handleInputChange}
                    required={view === 'signup'}
                    disabled={loading}
                    className="h-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 rounded-lg focus:border-zinc-600 focus:ring-zinc-600"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="auth-email" className="text-xs text-zinc-400 mb-1 block">
                  Email
                </Label>
                <Input
                  id="auth-email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  className="h-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 rounded-lg focus:border-zinc-600 focus:ring-zinc-600"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="auth-password" className="text-xs text-zinc-400">
                    Password
                  </Label>
                  {view === 'login' && (
                    <Link href="/forgot-password" className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors">
                      Forgot?
                    </Link>
                  )}
                </div>
                <Input
                  id="auth-password"
                  name="password"
                  type="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  className="h-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 rounded-lg focus:border-zinc-600 focus:ring-zinc-600"
                />
              </div>

              {error && (
                <div className="text-red-400 text-xs py-1">{error}</div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-10 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold rounded-lg border border-zinc-700 transition-all"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : view === 'signup' ? (
                  'Create Account'
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            {/* Toggle view */}
            <div className="text-center mt-3 text-sm text-zinc-500">
              {view === 'signup' ? (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => { setView('login'); setError(''); }}
                    className="text-white hover:underline font-medium"
                  >
                    Sign in
                  </button>
                </>
              ) : (
                <>
                  Don&apos;t have an account?{' '}
                  <button
                    type="button"
                    onClick={() => { setView('signup'); setError(''); }}
                    className="text-white hover:underline font-medium"
                  >
                    Sign up
                  </button>
                </>
              )}
            </div>

            {/* Terms */}
            <p className="text-center text-[11px] text-zinc-600 mt-3 leading-relaxed">
              By continuing, you agree to our{' '}
              <Link href="/terms" className="underline hover:text-zinc-400 transition-colors">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link href="/privacy" className="underline hover:text-zinc-400 transition-colors">
                Privacy Policy
              </Link>
              .
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
